import { Box } from '../Box/Box'
import { CSSProperties, ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react'
import { TextField, TextFieldProps } from '../TextField/TextField'
import { VALID_HEX_PARTIAL, filterColor, validateHex } from './util'
import { styled } from '@mui/system'
import { useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MuiIconButton from '@mui/material/IconButton'

export type ColorPickerProps = Omit<TextFieldProps, 'adornment' | 'onChange'> & {
  onChange: (x: string) => void
  allowEmpty?: boolean
  hideRemoveButton?: boolean
}

/**
 * ColorPicker is a styled variant of MUI's TextField.
 *
 * More info can be found at: https://mui.com/material-ui/react-text-field/
 */

const StyledNativeColorInput = styled('input')`
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  cursor: pointer;
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    padding: 0;
    border-radius: 0.175rem;
    border: 0;
  }
  &::-moz-color-swatch {
    padding: 0;
    border-radius: 0.175rem;
    border: 0;
  }
  appearance: none;
  z-index: 0;
`

const ColorPickerStartAdornment: FC<{
  color?: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  label?: string
  id?: string
}> = ({ color, onChange, label, id }) => {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Box
      sx={theme => ({ position: 'relative', height: theme.spacing(6), width: theme.spacing(6) })}
    >
      <Box
        sx={theme => ({
          position: 'absolute',
          cursor: 'pointer',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: 1,
          borderColor: 'border.outline',
          borderRadius: theme.spacing(0.5),
          background:
            color ||
            'conic-gradient(from 180deg at 50% 50%, #D74646 0deg, #FF8A00 48.75deg, #FAFF00 103.13deg, #00FF19 146.25deg, #00F0FF 191.25deg, #0047FF 240deg, #2700FF 288.75deg, #D74646 360deg)'
        })}
        onClick={() => inputRef.current?.click()}
      />
      <StyledNativeColorInput
        ref={inputRef}
        value={color}
        onChange={onChange}
        type="color"
        aria-label={label ? `${label}-color-picker` : 'native-color-picker'}
        aria-labelledby={id ?? undefined}
      />
    </Box>
  )
}

export const ColorPicker: FC<ColorPickerProps> = ({
  id,
  value,
  defaultValue,
  onChange,
  label,
  allowEmpty = false,
  hideRemoveButton = false,
  ...rest
}: ColorPickerProps) => {
  const theme = useTheme()
  const initialColor = filterColor(value ?? defaultValue, allowEmpty)

  const [lastValidColorSelected, setLastValidColorSelected] = useState(initialColor)
  const [workingValue, setWorkingValue] = useState(initialColor)

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value.toUpperCase()
      const hexCode = value.replace('#', '')
      const isEmpty = !value.length
      const emptyValue = allowEmpty ? '' : '#'
      const final = isEmpty ? emptyValue : `#${hexCode}`

      /**
       * workingValue is the string currently in the keyboard text input
       * lastValidColorSelected is the correctly formatted hex color
       *
       * lastValidColorSelected only updates once a valid hex is found as a working value
       */
      if (VALID_HEX_PARTIAL.test(final)) setWorkingValue(final)

      if (validateHex(final) || (allowEmpty && !final.length)) {
        setLastValidColorSelected(final)
        onChange?.(final)
      }
    },
    [allowEmpty, onChange]
  )

  useEffect(() => {
    if (typeof value === 'string' && value.length > 0 && value !== lastValidColorSelected) {
      handleChange({ target: { value } } as any)
    }
  }, [value, handleChange, lastValidColorSelected])

  const injected = {
    ...rest,
    label,
    // Ensure the input is wide enough to show the full hex code
    style: { minWidth: theme.spacing(40) },
    inputProps: {
      maxLength: 7,
      style: { textTransform: 'uppercase' } as CSSProperties
    },
    onChange: handleChange,
    startAdornment: (
      <ColorPickerStartAdornment
        color={lastValidColorSelected}
        onChange={handleChange}
        label={label}
        id={id}
      />
    ),
    endAdornment:
      lastValidColorSelected?.length && !hideRemoveButton ? (
        <MuiIconButton
          sx={{ mr: -4 }}
          onClick={() => {
            if (allowEmpty) {
              setLastValidColorSelected('')
              setWorkingValue('')
              onChange?.('')
            } else {
              setLastValidColorSelected('#000000')
              setWorkingValue('#000000')
              onChange?.('#000000')
            }
          }}
          aria-label="clear selected color"
        >
          <CloseIcon />
        </MuiIconButton>
      ) : undefined
  }

  return <TextField {...injected} value={workingValue} />
}
